// TimingWaterfall — echarts 版 HAR-style 多行瀑布图。
//
// 用途：AI 日志 drawer / 非 AI 日志 drawer / provider analytics drawer。
// 单条调用 + 聚合平均 同一组件——传入对应字段即可（聚合时把 avg* 当
// 作 "代表性调用" 渲染）。
//
// 渲染规则（echarts custom series）：
//   yAxis 行：Gateway / Connect / Upstream wait / Generation —— 顺序即时间轴
//   xAxis：时间 ms；每行 bar 起点 = 上一行结束（HAR 风格累计）
//   Connect 行：内部强制按 DNS / TCP / TLS 拆 3 个子段，每段 min 宽度
//     ≥6px 即便耗时 1ms 也可见；子段间 1px 间隙
//   tooltip：hover 任意段 → 显示该段名称 + 耗时 + 占比；Connect 行
//     hover 任一子段 → 显示 DNS/TCP/TLS 全部明细
//
// props.data 字段（任意子集，null 段不画）：
//   gatewayMs / waitMs / generationMs / totalMs
//   dnsMs + tcpMs + tlsMs        ← 单次调用，Connect 行内拆分
//   connectMs                    ← 聚合平均（无细分），Connect 单段
//   reusedConnection / note
// props.labels 提供 i18n 文案；缺省值为英文。

import React from 'react';
import * as echarts from 'echarts/core';
import { CustomChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([CustomChart, GridComponent, TooltipComponent, CanvasRenderer]);

const DEFAULT_LABELS = {
  gateway: 'Gateway',
  connect: 'Connect',
  dns: 'DNS',
  tcp: 'TCP',
  tls: 'TLS',
  wait: 'Upstream wait',
  generation: 'Generation',
  reusedConn: 'Connection reused (keep-alive)',
  nonStream:  'Non-streaming response (no generation phase)',
  total: 'Total',
};

// 颜色对齐 Chrome DevTools Network 瀑布的语义色板
//   queueing/stalled  → gray         (Gateway overhead)
//   DNS lookup        → teal / 青绿
//   Initial connect   → orange
//   SSL/TLS           → purple
//   Waiting (TTFB)    → green        (Upstream wait)
//   Content download  → blue         (Generation)
const COLORS = {
  gateway:    '#9aa0a6',  // gray
  dns:        '#1ba79c',  // teal
  tcp:        '#ff9c33',  // orange
  tls:        '#a76cd9',  // purple
  connect:    '#ff9c33',  // 聚合无细分时用 connecting 的 orange
  wait:       '#5fb965',  // green
  generation: '#4286f5',  // blue
};

const fmtMs = (ms) => {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
};

export const TimingWaterfall = ({ data = {}, labels = {}, compact = false }) => {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);
  const L = { ...DEFAULT_LABELS, ...labels };

  // 1. 构造阶段时间轴（顺序 = 视觉顺序）
  // 每个阶段产出 { key, label, color, start, end, subs? }
  // subs = [{label, ms, color}] 用于 Connect 行内部拆分
  const phases = React.useMemo(() => {
    const dns = data.dnsMs ?? null;
    const tcp = data.tcpMs ?? null;
    const tls = data.tlsMs ?? null;
    const hasSplit = [dns, tcp, tls].some((x) => x != null && x >= 0);

    let connectMs = null;
    let connectSubs = null;
    if (hasSplit) {
      connectMs = (dns || 0) + (tcp || 0) + (tls || 0);
      connectSubs = [
        { label: L.dns, ms: dns, color: COLORS.dns },
        { label: L.tcp, ms: tcp, color: COLORS.tcp },
        { label: L.tls, ms: tls, color: COLORS.tls },
      ].filter((s) => s.ms != null && s.ms >= 0);
    } else if (data.connectMs != null && data.connectMs >= 0) {
      connectMs = data.connectMs;
    }

    const out = [];
    let cursor = 0;
    const push = (key, label, color, ms, subs) => {
      if (ms == null || ms < 0) return;
      out.push({ key, label, color, start: cursor, end: cursor + ms, ms, subs });
      cursor += ms;
    };
    push('gateway', L.gateway, COLORS.gateway, data.gatewayMs ?? null);
    push('connect', L.connect, COLORS.connect, connectMs, connectSubs);
    push('wait', L.wait, COLORS.wait, data.waitMs ?? null);
    push('generation', L.generation, COLORS.generation, data.generationMs ?? null);
    return out;
  }, [data, L]);

  const totalMs = data.totalMs ?? (phases.length ? phases[phases.length - 1].end : 0);
  const xMax = Math.max(totalMs, phases.length ? phases[phases.length - 1].end : 0, 1);

  const rowH = compact ? 10 : 14;
  const rowGap = compact ? 8 : 10;
  const chartH = phases.length * (rowH + rowGap) + 6;

  // 2. 渲染 echarts
  React.useEffect(() => {
    if (!ref.current || phases.length === 0) return;
    const chart = echarts.init(ref.current, null, { renderer: 'canvas' });
    chartRef.current = chart;

    // 数据：每行一条记录 [start, end, label, color, subs]
    const rows = phases.map((p, i) => ({
      value: [p.start, p.end, p.label, p.color],
      itemKey: p.key,
      subs: p.subs,
      ms: p.ms,
      rowIndex: i,
    }));

    chart.setOption({
      animation: true,
      animationDuration: 350,
      grid: { left: 100, right: 70, top: 4, bottom: 4, containLabel: false },
      xAxis: {
        type: 'value',
        min: 0,
        max: xMax,
        show: false,
      },
      yAxis: {
        type: 'category',
        data: phases.map((p) => p.label),
        inverse: true, // 第一项在最上
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9aa0a8', fontSize: compact ? 10 : 11 },
        splitLine: { show: false },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(28,28,32,0.96)',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        textStyle: { color: '#eee', fontSize: 12 },
        formatter: (p) => {
          // p.data is the row record. Subs = full breakdown.
          const d = p.data;
          if (!d) return '';
          const pctOfTotal = totalMs > 0 ? ((d.ms / totalMs) * 100).toFixed(1) : '—';
          let html = `<div style="font-weight:600;margin-bottom:4px">${phases[d.rowIndex].label}</div>`;
          html += `<div>${fmtMs(d.ms)} <span style="color:#9aa0a8">· ${pctOfTotal}% of total</span></div>`;
          if (d.subs && d.subs.length > 1) {
            html += '<div style="margin-top:6px;padding-top:6px;border-top:1px dashed rgba(255,255,255,0.15)">';
            for (const s of d.subs) {
              const sPct = d.ms > 0 ? ((s.ms / d.ms) * 100).toFixed(1) : '0';
              html += `<div style="display:flex;justify-content:space-between;gap:18px;font-size:11px">
                <span style="color:#bbb"><span style="display:inline-block;width:8px;height:8px;background:${s.color};border-radius:1px;margin-right:6px;vertical-align:middle"></span>${s.label}</span>
                <span class="mono" style="color:#eee">${fmtMs(s.ms)} <span style="color:#888">(${sPct}%)</span></span>
              </div>`;
            }
            html += '</div>';
          }
          return html;
        },
      },
      series: [
        {
          type: 'custom',
          name: 'timing',
          renderItem: (params, api) => {
            const rowIdx = params.dataIndex;
            const row = rows[rowIdx];
            const startX = api.coord([api.value(0), rowIdx])[0];
            const endX = api.coord([api.value(1), rowIdx])[0];
            const yMid = api.coord([0, rowIdx])[1];
            const barTop = yMid - rowH / 2;
            const phaseW = endX - startX;

            const children = [];

            if (row.subs && row.subs.length > 0) {
              // Connect 行：内部按子段比例画 + min width 保证可见
              const totalSubMs = row.subs.reduce((a, s) => a + (s.ms || 0), 0) || 1;
              const minSegW = 6; // 最小段宽 px（即使 1ms 也能看见）
              // 第一遍：按真实比例算宽，记录哪些低于 minSegW
              const widths = row.subs.map((s) => (s.ms / totalSubMs) * phaseW);
              const underfill = widths.reduce((acc, w, i) => acc + Math.max(0, minSegW - w), 0);
              const overflow = widths.reduce((acc, w) => acc + Math.max(0, w - minSegW), 0);
              // 把不足部分从大段里按比例扣
              const adjusted = widths.map((w) => {
                if (w >= minSegW) return w - (w / overflow) * underfill;
                return minSegW;
              });
              let cx = startX;
              for (let i = 0; i < row.subs.length; i++) {
                const s = row.subs[i];
                const w = Math.max(adjusted[i], minSegW);
                children.push({
                  type: 'rect',
                  shape: { x: cx, y: barTop, width: Math.max(w - 1, 1), height: rowH },
                  style: { fill: s.color },
                });
                cx += w;
              }
            } else {
              // 普通行：单段
              children.push({
                type: 'rect',
                shape: { x: startX, y: barTop, width: Math.max(phaseW, 1), height: rowH },
                style: { fill: api.value(3) },
              });
            }

            // 时间标签：贴在 bar 末端右侧 6px（像 Chrome DevTools 网络瀑布）
            children.push({
              type: 'text',
              style: {
                x: endX + 6,
                y: yMid,
                text: fmtMs(row.ms),
                fill: '#cfd2d6',
                font: `${compact ? 10 : 11}px ui-monospace, SFMono-Regular, Menlo, monospace`,
                textVerticalAlign: 'middle',
                textAlign: 'left',
              },
            });

            return {
              type: 'group',
              children,
            };
          },
          encode: { x: [0, 1], y: 2 },
          data: rows,
        },
      ],
    });

    const onResize = () => chart.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, [phases, xMax, rowH, compact, totalMs]);

  return (
    <div style={{ margin: '4px 0 8px' }}>
      {data.reusedConnection && (
        <div style={{ fontSize: 10.5, color: 'var(--fg-4)', marginBottom: 6 }}>
          ⚡ {L.reusedConn}
        </div>
      )}
      {data.isStreaming === false && (
        <div style={{ fontSize: 10.5, color: 'var(--fg-4)', marginBottom: 6 }}>
          📦 {L.nonStream}
        </div>
      )}
      {phases.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--fg-4)', padding: '6px 0' }}>—</div>
      ) : (
        <div ref={ref} style={{ width: '100%', height: chartH }} />
      )}
      {(data.note || data.totalMs != null) && (
        <div className="row between" style={{ marginTop: 6, fontSize: compact ? 10 : 11, color: 'var(--fg-4)' }}>
          <span className="mono">{data.note || ''}</span>
          {data.totalMs != null && <span className="mono">{L.total}: {fmtMs(data.totalMs)}</span>}
        </div>
      )}
    </div>
  );
};

export default TimingWaterfall;
